import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Settings,
  Archive,
  BarChart3,
  Shield,
  ClipboardList,
  Layers,
  Gavel,
  ShieldCheck,
  ArrowRightLeft,
  ChevronRight,
  GraduationCap,
  BookOpen,
  Award,
  Megaphone,
  Contact,
  Tag,
  MessageCircle,
  FileText
} from 'lucide-react';
import { ScrollText } from 'lucide-react';
import { User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import IconButton from './IconButton';

// Ícone estilo Google Apps (grade 3x3 de pontos)
const GoogleAppsIcon = ({ className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <circle cx="5" cy="5" r="1.5" />
    <circle cx="12" cy="5" r="1.5" />
    <circle cx="19" cy="5" r="1.5" />

    <circle cx="5" cy="12" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="19" cy="12" r="1.5" />

    <circle cx="5" cy="19" r="1.5" />
    <circle cx="12" cy="19" r="1.5" />
    <circle cx="19" cy="19" r="1.5" />
  </svg>
);

// Submenu do módulo Seguros
const segurosSubItems = [
  { name: 'Faixas', icon: Layers, path: '/faixas', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20' },
  { name: 'Grupo', icon: ShieldCheck, path: '/gruposegur', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
  { name: 'Regras', icon: Gavel, path: '/regras', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20' },
  { name: 'Movimentação', icon: ArrowRightLeft, path: '/movimentacao', color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-900/20' },
  { name: 'Consolidação', icon: Archive, path: '/consolidacao', color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-900/20' },
  { name: 'Convenção', icon: ScrollText, path: '/convencao', color: 'text-cyan-600 dark:text-cyan-400', bg: 'bg-cyan-50 dark:bg-cyan-900/20' }
];

const segurosPaths = segurosSubItems.map(s => s.path);

// Submenu do módulo Educação
const educacaoSubItems = [
  { name: 'Cursos', icon: BookOpen, path: '/cursos', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20' },
  { name: 'Participantes', icon: Users, path: '/participantes', color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/20' },
  { name: 'Modelo de Certificado', icon: Award, path: '/cert-modelos', color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-900/20' }
];

const educacaoPaths = educacaoSubItems.filter(s => s.path).map(s => s.path);

const marketingSubItems = [
  { name: 'Visão geral', icon: BarChart3, path: '/marketing', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20' },
  { name: 'Campanhas', icon: Megaphone, path: '/marketing/campanhas', color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-900/20' },
  { name: 'Contatos', icon: Contact, path: '/marketing/contatos', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
  { name: 'Grupos', icon: Tag, path: '/marketing/grupos', color: 'text-teal-600 dark:text-teal-400', bg: 'bg-teal-50 dark:bg-teal-900/20' },
  { name: 'Mensagens', icon: MessageCircle, path: '/marketing/mensagens', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20' },
  { name: 'Relatórios', icon: FileText, path: '/marketing/relatorios', color: 'text-cyan-600 dark:text-cyan-400', bg: 'bg-cyan-50 dark:bg-cyan-900/20' },
  { name: 'Configurações', icon: Settings, path: '/marketing/configuracoes', color: 'text-gray-600 dark:text-gray-400', bg: 'bg-gray-100 dark:bg-gray-700/50' }
];

const marketingPaths = marketingSubItems.map(s => s.path);

const Sidebar = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [segurosOpen, setSegurosOpen] = useState(false);
  const [educacaoOpen, setEducacaoOpen] = useState(false);
  const [marketingOpen, setMarketingOpen] = useState(false);
  const segurosRef = useRef(null);

  const handleMenuItemClick = () => {
    if (isOpen) {
      onToggle();
    }
  };

  // Fechar submenus ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (segurosRef.current && !segurosRef.current.contains(event.target)) {
        setSegurosOpen(false);
        setEducacaoOpen(false);
        setMarketingOpen(false);
      }
    };
    if (segurosOpen || educacaoOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [segurosOpen, educacaoOpen]);

  const menuItems = [
    {
      name: 'Análise Seguros',
      icon: LayoutDashboard,
      path: '/dashboard',
      roles: ['admin', 'user']
    },
    {
      name: 'Demandas',
      icon: ClipboardList,
      path: '/demandas',
      roles: ['admin', 'user']
    },
    // Módulo Seguros (com submenu)
    {
      name: 'Seguros',
      icon: ShieldCheck,
      path: null,
      roles: ['admin', 'user'],
      isGroup: true,
      group: 'seguros'
    },
    // Módulo Educação (com submenu)
    {
      name: 'Educação',
      icon: GraduationCap,
      path: null,
      roles: ['admin', 'user'],
      isGroup: true,
      group: 'educacao'
    },
    {
      name: 'Marketing',
      icon: Megaphone,
      path: null,
      roles: ['admin', 'user'],
      isGroup: true,
      group: 'marketing'
    },
    {
      name: 'Cliente',
      icon: User,
      path: '/cliente',
      roles: ['admin', 'user']
    },
    {
      name: 'Relatório',
      icon: BarChart3,
      path: '/relatorio',
      roles: ['admin', 'user']
    },
    {
      name: 'Usuários',
      icon: Users,
      path: '/usuarios',
      roles: ['admin']
    },
    {
      name: 'Permissões',
      icon: Shield,
      path: '/permissoes',
      roles: ['admin']
    },
    {
      name: 'Configurações',
      icon: Settings,
      path: '/configuracoes',
      roles: ['admin', 'user']
    }
  ];

  const filteredMenuItems = menuItems.filter(item =>
    item.roles.includes(user?.role)
  );

  const isActive = (path) => {
    if (!path) return false;
    return location.pathname === path;
  };

  // Verifica se alguma aplicação de um módulo está ativa
  const isSegurosActive = segurosPaths.includes(location.pathname);
  const isEducacaoActive = educacaoPaths.includes(location.pathname);
  const isMarketingActive = marketingPaths.includes(location.pathname);

  // Configuração dos grupos de submenu
  const groupConfig = {
    seguros: {
      open: segurosOpen,
      setOpen: setSegurosOpen,
      items: segurosSubItems,
      isActive: isSegurosActive,
      headerTitle: 'Módulo Seguros',
      headerIcon: ShieldCheck,
      gradient: 'from-primary-600 to-indigo-600 dark:from-primary-700 dark:to-indigo-700',
      iconGradient: 'from-primary-500 to-indigo-600'
    },
    educacao: {
      open: educacaoOpen,
      setOpen: setEducacaoOpen,
      items: educacaoSubItems,
      isActive: isEducacaoActive,
      headerTitle: 'Módulo Educação',
      headerIcon: GraduationCap,
      gradient: 'from-amber-500 to-orange-600 dark:from-amber-600 dark:to-orange-700',
      iconGradient: 'from-amber-500 to-orange-600'
    },
    marketing: {
      open: marketingOpen,
      setOpen: setMarketingOpen,
      items: marketingSubItems,
      isActive: isMarketingActive,
      headerTitle: 'Módulo Marketing',
      headerIcon: Megaphone,
      gradient: 'from-blue-600 to-violet-600 dark:from-blue-700 dark:to-violet-700',
      iconGradient: 'from-blue-500 to-violet-600'
    }
  };

  return (
    <div
      ref={segurosRef}
      className={`relative bg-white dark:bg-gray-800 shadow-lg transition-all duration-300 ease-in-out ${
        isOpen ? 'w-64' : 'w-16'
      } flex flex-col flex-shrink-0`}
    >
      {/* Header */}
      <div className="flex items-center justify-start p-4 border-b border-gray-200 dark:border-gray-700">
        <IconButton
          icon={GoogleAppsIcon}
          onClick={onToggle}
          variant="primary"
          size="sm"
          title={isOpen ? 'Recolher menu' : 'Expandir menu'}
          className="w-8 h-8"
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {filteredMenuItems.map((item) => {
          const Icon = item.icon;

          // Item de grupo (Seguros / Educação)
          if (item.isGroup) {
            const gcfg = groupConfig[item.group];
            const active = gcfg.isActive;
            return (
              <div key={item.name} className="relative">
                <button
                  onClick={() => {
                    gcfg.setOpen(prev => !prev);
                    // Fechar outros grupos
                    if (item.group === 'seguros') {
                      setEducacaoOpen(false);
                      setMarketingOpen(false);
                    } else if (item.group === 'educacao') {
                      setSegurosOpen(false);
                      setMarketingOpen(false);
                    } else {
                      setSegurosOpen(false);
                      setEducacaoOpen(false);
                    }
                  }}
                  className={`sidebar-item w-full text-left ${
                    active ? 'sidebar-item-active' : 'sidebar-item-inactive'
                  } ${!isOpen ? 'justify-center' : ''} ${
                    gcfg.open ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                  }`}
                  title={!isOpen ? item.name : ''}
                >
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-md bg-gradient-to-br ${gcfg.iconGradient} ${!isOpen ? '' : 'mr-3'} flex-shrink-0`}>
                    <Icon className="w-3.5 h-3.5 text-white" />
                  </span>
                  {isOpen && (
                    <span className="flex-1 transition-opacity duration-300">
                      {item.name}
                    </span>
                  )}
                  {isOpen && (
                    <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${gcfg.open ? 'rotate-90' : ''}`} />
                  )}
                </button>

                {/* Submenu flutuante do módulo */}
                {gcfg.open && (
                  <div className="absolute top-0 left-full ml-1 z-50 animate-fade-in">
                    <div className="w-56 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                      {/* Cabeçalho do submenu */}
                      <div className={`px-4 py-3 bg-gradient-to-r ${gcfg.gradient}`}>
                        <div className="flex items-center">
                          <gcfg.headerIcon className="w-5 h-5 text-white mr-2" />
                          <span className="text-white font-semibold text-sm">{gcfg.headerTitle}</span>
                        </div>
                      </div>
                      {/* Itens do submenu */}
                      <div className="p-2 space-y-1 max-h-[70vh] overflow-y-auto">
                        {gcfg.items.map((sub) => {
                          const SubIcon = sub.icon;
                          const subActive = sub.path && isActive(sub.path);
                          if (sub.disabled) {
                            return (
                              <div
                                key={sub.name}
                                className="flex items-center px-3 py-2.5 rounded-lg text-sm font-medium opacity-50 cursor-not-allowed"
                              >
                                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${sub.bg} ${sub.color} mr-3 flex-shrink-0`}>
                                  <SubIcon className="w-4 h-4" />
                                </span>
                                <span className="flex-1 text-gray-500 dark:text-gray-400">{sub.name}</span>
                                <span className="text-[10px] uppercase tracking-wide text-gray-400">em breve</span>
                              </div>
                            );
                          }
                          return (
                            <Link
                              key={sub.path}
                              to={sub.path}
                              onClick={() => {
                                gcfg.setOpen(false);
                                handleMenuItemClick();
                              }}
                              className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                                subActive
                                  ? `${sub.bg} ${sub.color} ring-1 ring-inset ring-current/20`
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/60'
                              }`}
                            >
                              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${sub.bg} ${sub.color} mr-3 flex-shrink-0`}>
                                <SubIcon className="w-4 h-4" />
                              </span>
                              <span className="flex-1">{sub.name}</span>
                              {subActive && (
                                <span className="w-2 h-2 rounded-full bg-current opacity-60" />
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          }

          // Item normal
          const active = isActive(item.path);
          return (
            <Link
              key={item.name}
              to={item.path}
              onClick={handleMenuItemClick}
              className={`sidebar-item ${
                active ? 'sidebar-item-active' : 'sidebar-item-inactive'
              } ${!isOpen ? 'justify-center' : ''}`}
              title={!isOpen ? item.name : ''}
            >
              <Icon className={`w-5 h-5 ${!isOpen ? '' : 'mr-3'} flex-shrink-0`} />
              {isOpen && (
                <span className="transition-opacity duration-300">
                  {item.name}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User Info */}
      {isOpen && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
              <span className="text-gray-600 dark:text-gray-300 font-medium text-sm">
                {user?.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {user?.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user?.role === 'admin' ? 'Administrador' : 'Usuário'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
